// Component for parsing and displaying a custom ticker list.
// Handles parsing comma/space/newline-separated tickers, validation, and persistence.

import { useState, useEffect, useMemo, useCallback } from "react";

const STORAGE_KEY = "sp500_scanner_custom_tickers_v1";

/**
 * Parse a freeform text blob into a clean, deduped, validated ticker array.
 * Accepts comma, space, tab, newline, or semicolon separators.
 */
export function parseTickerText(text) {
  if (!text || typeof text !== "string") return [];
  const seen = new Set();
  const result = [];
  const tokens = text.split(/[\s,;]+/);
  for (const raw of tokens) {
    const clean = raw.trim().toUpperCase();
    if (!clean) continue;
    // Valid: 1-6 letters, optionally followed by . and 1-2 letters (BRK.B)
    if (!/^[A-Z]{1,6}(\.[A-Z]{1,2})?$/.test(clean)) continue;
    if (seen.has(clean)) continue;
    seen.add(clean);
    result.push(clean);
    if (result.length >= 200) break;
  }
  return result;
}

function loadPersistedText() {
  try {
    if (typeof localStorage === "undefined") return "";
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function savePersistedText(text) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, text);
  } catch {
    /* quota / privacy mode — ignore */
  }
}

/**
 * CustomTickerInput
 * @param {Object} props
 * @param {boolean} props.disabled - Disable while a scan is in flight
 * @param {(tickers: string[]) => void} props.onTickersChange - Fires whenever the parsed list changes
 */
export function CustomTickerInput({ disabled, onTickersChange }) {
  const [text, setText] = useState(() => loadPersistedText());

  const parsed = useMemo(() => parseTickerText(text), [text]);

  // Notify parent when parsed list changes
  useEffect(() => {
    onTickersChange?.(parsed);
  }, [parsed, onTickersChange]);

  // Persist text on change (debounced via effect timing)
  useEffect(() => {
    const t = setTimeout(() => savePersistedText(text), 300);
    return () => clearTimeout(t);
  }, [text]);

  const handleClear = useCallback(() => {
    setText("");
  }, []);

  const handleExample = useCallback(() => {
    setText("PLTR, CRWD, NET, DDOG, SNOW, MDB, SHOP, ABNB");
  }, []);

  const tooManyWarning = parsed.length >= 200;

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 6,
      padding: "10px 12px",
      borderRadius: 8,
      background: "var(--surface-1, rgba(0,0,0,0.03))",
      border: "0.5px solid var(--border, rgba(0,0,0,0.09))",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
      }}>
        <label style={{
          fontSize: 11, fontWeight: 600, color: "var(--text-secondary, #52525b)",
          textTransform: "uppercase", letterSpacing: "0.06em",
        }}>
          Custom tickers
        </label>
        <div style={{ display: "flex", gap: 6, fontSize: 10.5, color: "var(--text-muted, #71717a)" }}>
          <button
            onClick={handleExample}
            disabled={disabled}
            style={{
              background: "transparent",
              border: "0.5px solid var(--border, rgba(0,0,0,0.15))",
              padding: "2px 7px", borderRadius: 5, cursor: disabled ? "not-allowed" : "pointer",
              color: "#60a5fa", fontSize: 10, opacity: disabled ? 0.5 : 1,
            }}
          >
            Load example
          </button>
          <button
            onClick={handleClear}
            disabled={disabled || !text}
            style={{
              background: "transparent",
              border: "0.5px solid var(--border, rgba(0,0,0,0.15))",
              padding: "2px 7px", borderRadius: 5,
              cursor: (disabled || !text) ? "not-allowed" : "pointer",
              color: "#f87171", fontSize: 10, opacity: (disabled || !text) ? 0.5 : 1,
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={disabled}
        placeholder="Paste tickers: AAPL, MSFT, NVDA, PLTR&#10;or one per line, any separator"
        rows={3}
        style={{
          fontFamily: "var(--font-mono, monospace)",
          fontSize: 12,
          padding: "7px 9px",
          borderRadius: 6,
          border: "0.5px solid var(--border, rgba(0,0,0,0.15))",
          background: "var(--surface-2, white)",
          color: "var(--text-primary, #111)",
          resize: "vertical",
          minHeight: 60,
          outline: "none",
        }}
      />

      <div style={{
        display: "flex", justifyContent: "space-between",
        fontSize: 11, color: "var(--text-muted, #71717a)",
      }}>
        <span>
          {parsed.length > 0 ? (
            <>
              <span style={{ color: "#22c55e", fontWeight: 600 }}>{parsed.length}</span>
              {" "}valid ticker{parsed.length !== 1 ? "s" : ""} parsed
            </>
          ) : (
            text ? (
              <span style={{ color: "#f87171" }}>No valid tickers detected</span>
            ) : (
              <span>Enter 1-200 US stock tickers</span>
            )
          )}
        </span>
        {tooManyWarning && (
          <span style={{ color: "#f59e0b" }}>Max 200 — extras will be ignored</span>
        )}
      </div>

      {parsed.length > 0 && parsed.length <= 30 && (
        <div style={{
          fontSize: 10.5, color: "var(--text-muted, #71717a)",
          fontFamily: "var(--font-mono, monospace)",
          padding: "5px 7px",
          background: "rgba(0,0,0,0.03)",
          borderRadius: 4,
          wordBreak: "break-word",
        }}>
          {parsed.join(", ")}
        </div>
      )}
    </div>
  );
}
